import { useEffect, useState } from "react";
import axios from "axios";
import useReveal from "../../hooks/useReveal";
import { Quotes, Star, CaretLeft, CaretRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const { ref, visible } = useReveal();

  useEffect(() => {
    axios.get(`${API}/public/testimonials`).then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 7000);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length) return null;

  const go = (n) => setIdx((idx + n + items.length) % items.length);

  const photoUrl = (t) => {
    if (!t.photo_url) return null;
    return t.photo_url.startsWith("http") ? t.photo_url : `${BACKEND}${t.photo_url}`;
  };

  return (
    <section ref={ref} className="relative py-20 lg:py-28 bg-[#F8FAFC] overflow-hidden" data-testid="home-testimonials">
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#C2410C]/[0.05] rounded-full blur-3xl" aria-hidden />
      <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-[#0EA5E9]/[0.05] rounded-full blur-3xl" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`text-center max-w-2xl mx-auto transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C2410C]/10 text-[#C2410C] text-xs tracking-[0.2em] uppercase font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C2410C]" /> Testimonials
          </div>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1F2937] leading-[1.1]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            What our partners say
          </h2>
        </div>

        <div
          className={`mt-12 lg:mt-16 relative max-w-4xl mx-auto transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ transitionDelay: "150ms" }}
        >
          <div className="relative bg-white rounded-3xl border border-[#E2E8F0] p-8 sm:p-12 lg:p-14 shadow-sm" data-testid="testimonial-card">
            <Quotes size={48} weight="fill" className="absolute -top-5 left-8 sm:left-12 text-[#C2410C] bg-[#F8FAFC] p-2 rounded-full" />

            {items.map((t, i) => (
              <div key={t.id} className={`${i === idx ? "block" : "hidden"}`} style={{ animation: i === idx ? "fadeUp 0.6s ease-out both" : undefined }}>
                {t.rating > 0 && (
                  <div className="flex gap-0.5 mb-5">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star key={s} size={18} weight={s < t.rating ? "fill" : "regular"} className={s < t.rating ? "text-[#FB923C]" : "text-[#CBD5E1]"} />
                    ))}
                  </div>
                )}
                <blockquote className="text-lg sm:text-xl lg:text-2xl text-[#1F2937] leading-relaxed font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <div className="mt-8 flex items-center gap-4">
                  {photoUrl(t) ? (
                    <img src={photoUrl(t)} alt={t.name} loading="lazy" className="w-14 h-14 rounded-full object-cover ring-2 ring-[#C2410C]/20" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#C2410C] to-[#0EA5E9] flex items-center justify-center text-white font-bold text-lg" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      {t.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                    </div>
                  )}
                  <div>
                    <div className="font-bold text-[#1F2937]">{t.name}</div>
                    <div className="text-sm text-[#4B5563]">
                      {[t.role, t.company].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {items.length > 1 && (
            <div className="mt-6 flex items-center justify-center gap-3">
              <button onClick={() => go(-1)} className="w-11 h-11 rounded-full bg-white hover:bg-[#C2410C] hover:text-white border border-[#E2E8F0] flex items-center justify-center transition-all duration-200" aria-label="Previous" data-testid="testimonial-prev">
                <CaretLeft size={18} weight="bold" />
              </button>
              <div className="flex items-center gap-2">
                {items.map((_, i) => (
                  <button key={i} onClick={() => setIdx(i)} aria-label={`Testimonial ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-[#C2410C]" : "w-3 bg-[#CBD5E1] hover:bg-[#94A3B8]"}`} />
                ))}
              </div>
              <button onClick={() => go(1)} className="w-11 h-11 rounded-full bg-white hover:bg-[#C2410C] hover:text-white border border-[#E2E8F0] flex items-center justify-center transition-all duration-200" aria-label="Next" data-testid="testimonial-next">
                <CaretRight size={18} weight="bold" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
